import { HTMLProps } from "react";
import { z } from "zod";

export const igEventTypes = ["track"];
export const igEventSources = ["js_api"];

import { GeoLocationDto } from "../../../dtos/audiences";
import { AlertSeverity } from "../../../dtos/processes";
import { PluginOfferType } from "../config/offers";
import { VariationWithExperienceIdModel } from "../plugin";

const PluginLogLevelSchema = z.object({
  sentry: z.enum(["Debug", "Info", "Warning", "Error"]),
  console: z.enum(["Debug", "Info", "Warning", "Error"]),
});

export type ConsoleSeverity = Extract<
  "log" | "warn" | "error" | "dir" | "table" | "trace" | "debug",
  keyof Console
>;

export type PluginLogLevel = "preview" | "integration" | "local";

export type LoggerTypes = "Debug" | "Info" | "Warning" | "Error";

export type DevConsole = Console & {
  initialize: (logLevels: PluginLogLevel[]) => void;
};

export type LoggerFunction = {
  logWithFunction: (type: LoggerTypes, fn: string, message: string) => void;
  logUndefinedParams: (type: LoggerTypes, fn: string, params: object) => void;
};

export type StorageMethodType = "localStorage" | "cookies" | "static-cache";

export type IgNetworkRequestEventDetails<Body, Response> = {
  body: Body;
  response: Response;
};

export type TrackVariants = Array<
  | VariationWithExperienceIdModel
  | Omit<PluginOfferType, "tiers" | "offerProducts">
>;

export type RedirectTrackBody = {
  isEmpty?: boolean;
  redirectedFrom: string | null;
  redirectedTo: string | null;
  variationId?: string;
  documentReferrer?: string;
};

export interface IgEventType {
  type: (typeof igEventTypes)[number] | undefined;
  event: string;
  properties: any;
  source: (typeof igEventSources)[number] | undefined;
}

export interface TrackBodyModel {
  eventType: string;
  userId: string;
  variants: TrackVariants;
  newVariation?: VariationWithExperienceIdModel;
  version: string;
  buildId: number;
  initBuildId: number | null;
  preview: boolean | string;
  eventTimestamp: string;
  performanceNow: number;
  referrer: string;
  parsedReferrer?: LocationModel;
  storeName: string;
  themeId?: number;
  cartToken: string | null;
  location: LocationModel;
  device: DeviceModel;
  storeShopifyY?: string;
  isGoogleBot: boolean;
  firstVisitTs?: number;
  isFirstVisit?: boolean;
  pageView?: number;
  currency: string | false;
  country?: string;
  viewedProductPrices: string[];
  redirect?: RedirectTrackBody;
  pathnameOverride?: string;
  pageViewId?: string;
  sentDuring?: string;
  geoLocation?: GeoLocationDto;
  customEvent?: IgEventType;
  orgId: string;
}

export interface BrowserNameVersion {
  name: string;
  version: number;
}

export interface DeviceModel {
  os: BrowserNameVersion;
  browser: BrowserNameVersion;
  agent: string;
  useragent: string;
}

export interface LocationModel {
  origin: string;
  host: string;
  pathname: string;
  search: string;
  hash: string;
  queryParams: Record<string, string>;
}

export type InitialHTMLMap = Map<
  Element,
  [id: string, html: string, props?: HTMLProps<HTMLElement>]
>;

export type PluginMessageType =
  | "excluded"
  | "assigned"
  | "altTheme"
  | "previewTraffic"
  | "unassigned";

export type PluginMessage = {
  severity: AlertSeverity;
  type: PluginMessageType;
  message: string;
};

export type PluginTrafficMatchResponse =
  | {
      matched: false;
      by: string;
    }
  | {
      matched: true;
      by: string;
    };
